import React from 'react'
import { usePage } from '@inertiajs/react'
import { Toaster } from 'sonner'
import { useTheme } from '@/contexts/ThemeContext'
import { NotificationsProvider } from '@/contexts/NotificationsContext'
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/shadcn/sidebar'
import { TooltipProvider } from '@/components/ui/shadcn/tooltip'
import { Separator } from '@/components/ui/shadcn/separator'
import { AppSidebar } from '@/components/Layout/AppSidebar'
import { GodModeSidebar } from '@/components/Layout/GodModeSidebar'
import { ProfileDropdown } from '@/components/Layout/ProfileDropdown'
import { ThemeToggle } from '@/components/ui/ThemeToggle'
import { NotificationPermissionBanner } from '@/components/Layout/NotificationPermissionBanner'
import { ImpersonationBanner } from '@/components/Layout/ImpersonationBanner'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/shadcn/breadcrumb'
import type { CurrentUser, SharedProps } from '@/types/shared'

interface MainLayoutProps {
  children: React.ReactNode
  user: CurrentUser
  breadcrumbs?: { label: string; href?: string }[]
  godMode?: boolean
}

export default function MainLayout({
  children,
  user,
  breadcrumbs = [{ label: 'Inbox' }],
  godMode = false
}: MainLayoutProps) {
  const { theme } = useTheme()
  const { impersonating, true_user } = usePage<SharedProps>().props

  return (
    <NotificationsProvider>
      <TooltipProvider delayDuration={200}>
        <SidebarProvider className="h-svh overflow-hidden">
        {godMode ? <GodModeSidebar /> : <AppSidebar />}
        <SidebarInset className="min-h-0">
          {impersonating && true_user && (
            <ImpersonationBanner trueUser={true_user} />
          )}
          <header className="bg-background sticky top-0 flex shrink-0 items-center gap-2 border-b p-4">
          <SidebarTrigger className="-ml-1" />
          <Separator
            orientation="vertical"
            className="mr-2 data-[orientation=vertical]:h-4"
          />
          <Breadcrumb>
            <BreadcrumbList>
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <BreadcrumbSeparator className="hidden md:block" />}
                  <BreadcrumbItem className={index < breadcrumbs.length - 1 ? "hidden md:block" : ""}>
                    {crumb.href ? (
                      <BreadcrumbLink href={crumb.href}>{crumb.label}</BreadcrumbLink>
                    ) : (
                      <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                    )}
                  </BreadcrumbItem>
                </React.Fragment>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle />
            <ProfileDropdown user={user} />
          </div>
        </header>
        <NotificationPermissionBanner />
        <div className="flex flex-1 flex-col min-h-0">
          {children}
        </div>
      </SidebarInset>
        <Toaster
          position="bottom-center"
          richColors
          theme={theme}
          toastOptions={{
            style: {
              background: 'var(--card)',
              border: '1px solid var(--border)',
              color: 'var(--foreground)',
            },
          }}
        />
      </SidebarProvider>
      </TooltipProvider>
    </NotificationsProvider>
  )
}
